import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

export default function TermsOfServicePage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - FuturePath Technologies</title>
        <meta name="description" content="Terms of Service for FuturePath Technologies - the terms governing use of our website and services." />
      </Helmet>

      <section className="relative min-h-[30vh] flex items-center justify-center pt-32 pb-12">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last updated: August 4, 2026
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-invert max-w-none space-y-10"
          >

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">1. Agreement to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms of Service ("Terms") govern your use of the FuturePath Technologies website and any services provided by FuturePath Technologies ("we," "us," "our"). By using our website or engaging our services, you agree to these Terms. Specific project engagements will be governed additionally by a separate written proposal or contract, which takes precedence over these general Terms in case of conflict.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">2. Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                We provide software development and consulting services, including but not limited to e-commerce platform development, custom software, and related technical services. Specific scope, deliverables, timelines, and pricing for any engagement will be defined in a separate written proposal or agreement signed by both parties.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">3. Payment Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                Payment terms, including milestones and due dates, will be specified in the individual project proposal or contract. Late payments may result in pausing of work until outstanding amounts are settled. Any specific late-payment fees, if applicable, will be stated in the project agreement.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">4. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                Unless otherwise agreed in writing, ownership of custom code, designs, and deliverables created specifically for a client transfers to the client upon full and final payment. We retain the right to use general knowledge, pre-existing tools, frameworks, and non-client-specific code developed independently of the engagement. We may reference completed projects in our portfolio and marketing materials unless the client requests confidentiality in writing.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">5. Client Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed">
                Clients agree to provide timely feedback, access to necessary systems/accounts, and accurate project requirements. Delays caused by the client may affect project timelines and are not the responsibility of FuturePath Technologies.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">6. Confidentiality</h2>
              <p className="text-muted-foreground leading-relaxed">
                We treat client project information as confidential and will not disclose proprietary business information shared during an engagement without consent, except as required by law. A mutual Non-Disclosure Agreement (NDA) is available upon request before detailed project discussions.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">7. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, FuturePath Technologies shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services or website. Our total liability for any claim related to a specific engagement shall not exceed the total fees paid by the client for that engagement.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">8. Warranties and Disclaimers</h2>
              <p className="text-muted-foreground leading-relaxed">
                We will perform services with reasonable skill and care consistent with industry standards. Except as expressly stated in a project agreement, our services and website are provided "as is" without warranties of any kind, express or implied.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">9. Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                Either party may terminate an ongoing engagement as specified in the individual project agreement. Upon termination, the client is responsible for payment for work completed up to the termination date.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">10. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by the laws of India. Any disputes arising from these Terms or a project engagement shall be subject to the exclusive jurisdiction of the courts located in [your city/state], India.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">11. Changes to These Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update these Terms from time to time. Continued use of our website or services after changes are posted constitutes acceptance of the revised Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold font-outfit text-white mb-3">12. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about these Terms, please contact us at: <br />
                <span className="text-white">[your business email]</span>
              </p>
            </div>

          </motion.div>
        </div>
      </section>
    </>
  );
}
